import React, { useState } from 'react';
import { Product, IntegrationField } from '../../types';
import { 
  ChevronDown, 
  ChevronUp, 
  Key, 
  Database, 
  Link as LinkIcon, 
  Lock, 
  Code,
  Copy,
  ExternalLink
} from 'lucide-react';
import Button from '../ui/Button';
import { formatDate } from '../../utils/format';

interface ProductDetailViewProps {
  product: Product;
  onEdit?: () => void;
  onClose?: () => void;
}

// Create a section component for collapsible sections
interface SectionProps {
  title: string;
  leftIcon?: React.ReactNode;
  children: React.ReactNode;
  badgeCount?: number;
  badgeColor?: string;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
}

const DetailSection: React.FC<SectionProps> = ({
  title,
  leftIcon,
  children,
  badgeCount,
  badgeColor = 'bg-primary-700/30 text-primary-300',
  defaultOpen = true,
  actions
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-card-darker border border-border-dark rounded-lg mb-6 overflow-hidden">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-hover/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          <h3 className="text-lg font-medium">{title}</h3>
          {typeof badgeCount !== 'undefined' && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${badgeCount > 0 ? badgeColor : 'bg-background-hover text-text-muted-dark'}`}>
              {badgeCount} {badgeCount === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>
        <div className="flex items-center">
          {actions}
          <button type="button" className="ml-2 p-1">
            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>
      
      {isOpen && (
        <div className="p-4 border-t border-border-dark/50">
          {children}
        </div>
      )}
    </div>
  );
};

// Group fields by category for better organization
const groupFieldsByCategory = (fields: IntegrationField[] = []) => {
  const groups: Record<string, IntegrationField[]> = {
    authentication: [],
    configuration: [],
    mapping: [],
    other: []
  };

  fields.forEach(field => {
    // Simple categorization based on field key or type
    if (field.type === 'soar-mapping') {
      groups.mapping.push(field);
    } else if (field.field_key.includes('key') || field.field_key.includes('token') || field.field_key.includes('secret') || field.type === 'secret') {
      groups.authentication.push(field);
    } else if (field.field_key === 'services' || field.field_key.includes('config') || field.field_key.includes('url')) {
      groups.configuration.push(field);
    } else {
      groups.other.push(field);
    }
  });

  return groups;
};

// Copy text to clipboard
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    // Could add a toast notification here
            // Copied to clipboard
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};

const ProductDetailView: React.FC<ProductDetailViewProps> = ({ 
  product,
  onEdit,
  onClose
}) => {
  // Group integration fields by category
  const fieldGroups = groupFieldsByCategory(product.integration_fields);
  
  // Extract tags from product
  const tags = product.tags ? Object.entries(product.tags).map(([key, value]) => ({ key, value })) : [];

  // Render field value based on type
  const renderFieldValue = (field: IntegrationField) => {
    if (field.type === 'secret') {
      return (
        <div className="flex items-center">
          <div className="form-input bg-background-dark py-2">••••••••</div>
          <button
            type="button"
            className="ml-2 p-1.5 bg-background-hover rounded hover:bg-background-dark transition-colors"
            onClick={() => copyToClipboard(field.default_value || '')}
            title="Copy value"
          >
            <Copy size={14} />
          </button>
        </div>
      );
    } else if (field.type === 'multi-select') {
      return (
        <div className="form-input bg-background-dark flex flex-wrap gap-1 min-h-[40px]">
          {field.options && field.options.length > 0 ? (
            field.options.map((option, idx) => (
              <span key={idx} className="bg-primary-700/30 text-primary-300 px-2 py-0.5 rounded text-xs">
                {option}
              </span>
            ))
          ) : (
            <span className="text-text-muted-dark text-sm">No options defined</span>
          )}
        </div>
      );
    } else if (field.type === 'url') {
      return (
        <div className="flex items-center">
          <div className="form-input bg-background-dark py-2 flex-grow overflow-x-auto">
            {field.default_value || '—'}
          </div>
          {field.default_value && (
            <a
              href={field.default_value}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 p-1.5 bg-background-hover rounded hover:bg-background-dark transition-colors"
              title="Open URL"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      );
    } else if (field.type === 'soar-mapping') {
      return (
        <div className="bg-background-dark p-3 rounded border border-gray-700">
          <div className="mb-2 text-xs font-medium text-gray-400">Field Mappings:</div>
          {field.field_mapping && Object.keys(field.field_mapping).length > 0 ? (
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="text-xs font-medium text-gray-400">Log Field</div>
                <div className="text-xs font-medium text-gray-400 text-center">→</div>
                <div className="text-xs font-medium text-gray-400">SOAR Field</div>
              </div>
              {Object.entries(field.field_mapping).map(([key, value], idx) => (
                <div key={idx} className="grid grid-cols-3 gap-3 items-center">
                  <span className="font-mono bg-background-darker px-2 py-0.5 rounded text-green-300">{value}</span>
                  <span className="text-gray-400 text-center">→</span>
                  <span className="font-mono bg-background-darker px-2 py-0.5 rounded text-blue-300">{key}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-text-muted-dark text-sm">No mappings defined</span>
          )}
        </div>
      );
    } else {
      return (
        <div className="form-input bg-background-dark py-2">
          {field.default_value || '—'}
        </div>
      );
    }
  };

  return (
    <div className="product-detail-view space-y-6">
      {/* Basic Information Section */}
      <DetailSection 
        title="Basic Information" 
        leftIcon={<Database size={18} className="text-primary" />}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Basic info */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <div className="flex items-center mb-6">
                {product.logo_url ? (
                  <img
                    src={product.logo_url}
                    alt={`${product.name} logo`}
                    className="h-16 w-16 object-contain bg-white rounded p-1 border border-border-dark mr-4"
                  />
                ) : (
                  <div className="h-16 w-16 border border-dashed border-border-dark rounded flex items-center justify-center bg-background-hover mr-4">
                    <span className="text-gray-500">No logo</span>
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-semibold">{product.name}</h2>
                  <div className="text-sm text-gray-400 mt-1">
                    {product.status && (
                      <span className={`px-2 py-0.5 rounded-full text-xs mr-2 ${
                        product.status === 'active' ? 'bg-green-900/30 text-green-400' : 'bg-gray-700/50 text-gray-400'
                      }`}>
                        {product.status}
                      </span>
                    )}
                    {product.created_at && (
                      <span>Created {formatDate(product.created_at)}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Product ID</label>
                  <div className="flex items-center">
                    <div className="bg-background-dark text-sm px-3 py-2 rounded-md border border-gray-700 flex-grow font-mono">
                      {product.id || product._id || '—'}
                    </div>
                    <button
                      type="button"
                      className="ml-2 p-1.5 bg-background-hover rounded hover:bg-background-dark transition-colors"
                      onClick={() => copyToClipboard(product.id || product._id || '')}
                      title="Copy ID"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                
                {product.logo_url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Logo URL</label>
                    <div className="flex items-center">
                      <div className="bg-background-dark text-sm px-3 py-2 rounded-md border border-gray-700 flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
                        {product.logo_url}
                      </div>
                      <a
                        href={product.logo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 p-1.5 bg-background-hover rounded hover:bg-background-dark transition-colors"
                        title="Open URL"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column - Tags */}
          <div className="lg:col-span-2">
            {/* Tags Section */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Product Tags</label>
              
              {tags.length > 0 ? (
                <div className="space-y-2">
                  {tags.map((tag, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="bg-gray-800/70 text-gray-300 px-3 py-2 rounded border border-gray-700 text-sm min-w-[120px]">
                        {tag.key}
                      </div>
                      <div className="bg-background-dark text-gray-300 px-3 py-2 rounded border border-gray-700 text-sm flex-grow">
                        {tag.value || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-text-muted-dark bg-background-dark rounded-md border border-gray-700">
                  <p>No tags defined</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DetailSection>

      {/* Authentication Fields Section */}
      {fieldGroups.authentication.length > 0 && (
        <DetailSection 
          title="Authentication Fields" 
          leftIcon={<Key size={18} className="text-amber-400" />}
          badgeCount={fieldGroups.authentication.length}
        >
          <div className="space-y-4">
            {fieldGroups.authentication.map((field) => (
              <div key={field.field_key}>
                <label className="block text-sm font-medium mb-1 flex items-center">
                  {field.label}
                  {field.is_required && (
                    <span className="text-red-400 ml-1">*</span>
                  )}
                  <span className="ml-2 text-xs text-gray-500 font-mono">{field.field_key}</span>
                </label>
                {renderFieldValue(field)}
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {/* Configuration Fields Section */}
      {fieldGroups.configuration.length > 0 && (
        <DetailSection 
          title="Configuration Fields" 
          leftIcon={<Database size={18} className="text-primary" />}
          badgeCount={fieldGroups.configuration.length}
        >
          <div className="space-y-4">
            {fieldGroups.configuration.map((field) => (
              <div key={field.field_key}>
                <label className="block text-sm font-medium mb-1 flex items-center">
                  {field.label}
                  {field.is_required && (
                    <span className="text-red-400 ml-1">*</span>
                  )}
                  <span className="ml-2 text-xs text-gray-500 font-mono">{field.field_key}</span>
                </label>
                {renderFieldValue(field)}
              </div>
            ))}
          </div>
        </DetailSection>
      )}
      
      {/* Other Fields Section */}
      {fieldGroups.other.length > 0 && (
        <DetailSection 
          title="Other Fields" 
          leftIcon={<LinkIcon size={18} className="text-blue-400" />}
          badgeCount={fieldGroups.other.length}
          defaultOpen={false}
        >
          <div className="space-y-4">
            {fieldGroups.other.map((field) => (
              <div key={field.field_key}>
                <label className="block text-sm font-medium mb-1 flex items-center">
                  {field.label}
                  {field.is_required && (
                    <span className="text-red-400 ml-1">*</span>
                  )}
                  <span className="ml-2 text-xs text-gray-500 font-mono">{field.field_key}</span>
                </label>
                {renderFieldValue(field)}
              </div>
            ))}
          </div>
        </DetailSection>
      )}
          
      {/* SOAR Field Mappings Section */}
      {fieldGroups.mapping.length > 0 && (
        <DetailSection 
          title="SOAR Field Mappings" 
          leftIcon={<Code size={18} className="text-blue-400" />}
          badgeCount={fieldGroups.mapping.length}
          badgeColor="bg-blue-700/30 text-blue-300"
        >
          <div className="space-y-4">
            {fieldGroups.mapping.map((field) => (
              <div key={field.field_key}>
                {renderFieldValue(field)}
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {/* Action buttons */}
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-border-dark">
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        )}
        {onEdit && (
          <Button variant="primary" onClick={onEdit}>
            Edit Product
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductDetailView; 